/**
 * Block 12 PR-C Phase C7 Step 2 — FreeRowsSection.
 *
 * Editable rows table для FreeAnketaForm. Per методике §2.8 + ADR-0020 §Q9 —
 * свободный режим: user добавляет произвольные позиции (name/qty/unit/price),
 * cost вычисляется как `qty × price`. Total складывается выше (FreeAnketaFormView).
 *
 * UX:
 *   - Empty state: одна строка-плейсхолдер «Нет позиций. Нажмите «+ Добавить позицию»».
 *   - Per row: vertical card с 4 редактируемыми полями (name, qty+unit pair, price)
 *     + cost readonly + delete button (vertical layout per P-019 на mobile).
 *   - «+ Добавить позицию» button — добавляет пустую row.
 *
 * Reuse: TextInput / NumberInput from `components/forms/`. Никаких новых
 * primitives (P-022 frozen).
 *
 * Reference: ADR-0020 §Q9, методика §2.8.
 */

import { Trash2 } from 'lucide-react';
import { NumberInput } from '@/components/forms/NumberInput';
import { TextInput } from '@/components/forms/TextInput';
import type { FreeRow, FreeRowEditableField } from './types';

export interface FreeRowsSectionProps {
  rows: FreeRow[];
  onAdd: () => void;
  onUpdate: <F extends FreeRowEditableField>(id: string, field: F, value: FreeRow[F]) => void;
  onRemove: (id: string) => void;
}

export function FreeRowsSection({ rows, onAdd, onUpdate, onRemove }: FreeRowsSectionProps) {
  return (
    <section className="flex flex-col gap-3" aria-labelledby="free-rows-heading">
      <h3 id="free-rows-heading" className="text-ink font-bold text-lg">
        Позиции сметы
      </h3>

      {rows.length === 0 ? (
        <p className="text-muted text-sm" role="status">
          Нет позиций. Нажмите «+ Добавить позицию» чтобы создать первую.
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-3">
          {rows.map((row) => {
            const cost =
              Number.isFinite(row.quantity * row.price) && row.quantity * row.price >= 0
                ? row.quantity * row.price
                : 0;
            return (
              <li
                key={row.id}
                className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-3 flex flex-col gap-3"
                data-testid={`free-row-${row.id}`}
              >
                <TextInput
                  name={`free-name-${row.id}`}
                  label="Название позиции"
                  value={row.name}
                  onChange={(v) => onUpdate(row.id, 'name', v)}
                  placeholder="Например, ЛДСП"
                  maxLength={200}
                />
                <div className="flex flex-row gap-2">
                  <div className="flex-1">
                    <NumberInput
                      name={`free-qty-${row.id}`}
                      label="Количество"
                      value={Number.isFinite(row.quantity) ? row.quantity : null}
                      // NumberInput возвращает `null` при пустом поле; маппим в 0
                      // — пустое qty/price семантически = 0 для total. UX trade-off:
                      // user не может временно очистить поле во время редактирования
                      // (matches existing anketa material rows pattern).
                      onChange={(v) => onUpdate(row.id, 'quantity', v ?? 0)}
                      min={0}
                      step={0.1}
                    />
                  </div>
                  <div className="flex-1">
                    <TextInput
                      name={`free-unit-${row.id}`}
                      label="Ед. изм."
                      value={row.unit}
                      onChange={(v) => onUpdate(row.id, 'unit', v)}
                      maxLength={20}
                    />
                  </div>
                </div>
                <NumberInput
                  name={`free-price-${row.id}`}
                  label="Цена за единицу"
                  value={Number.isFinite(row.price) ? row.price : null}
                  onChange={(v) => onUpdate(row.id, 'price', v ?? 0)}
                  unit="₸"
                  min={0}
                />
                <div className="flex items-center justify-between">
                  <span className="text-ink text-base">
                    Стоимость:{' '}
                    <strong data-testid={`free-row-cost-${row.id}`}>
                      {cost.toLocaleString('ru-RU')}
                    </strong>{' '}
                    ₸
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemove(row.id)}
                    aria-label={`Удалить позицию «${row.name || 'без названия'}»`}
                    data-testid={`free-row-remove-${row.id}`}
                    className="min-h-tap min-w-tap flex items-center justify-center rounded-xl bg-error/10 text-error border border-error/40 px-3 focus-visible:ring-2 focus-visible:ring-error focus-visible:outline-none active:opacity-90"
                  >
                    <Trash2 size={20} aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={onAdd}
        aria-label="Добавить позицию"
        data-testid="free-row-add"
        className="w-full min-h-tap rounded-xl bg-cta/15 text-ink border-[1.5px] border-dashed border-cta px-4 py-3 font-semibold focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
      >
        + Добавить позицию
      </button>
    </section>
  );
}
